import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import "./AdminPanel.css";

const TABLES = [
    { name: "users", displayName: "Users", group: "Access", description: "Review accounts and update roles." },
    { name: "lodges", displayName: "Entities", group: "Archive", description: "Lodges and archive entities." },
    { name: "documenttypes", displayName: "Document Types", group: "Archive", description: "Document classification labels." },
    { name: "eventtypes", displayName: "Event Types", group: "Archive", description: "Meeting and event categories." },
    { name: "filetypes", displayName: "File Types", group: "Archive", description: "File format labels." },
    { name: "constitutions", displayName: "Constitutions", group: "Reference", description: "Masonic constitutions." },
    { name: "countries", displayName: "Countries", group: "Reference", description: "Country lookup values." },
    { name: "ordercodes", displayName: "Order Codes", group: "Reference", description: "Archive order codes." },
    { name: "officers", displayName: "Officers", group: "Membership", description: "Officer role definitions." },
    { name: "degrees", displayName: "Degrees", group: "Membership", description: "Degree definitions." },
    { name: "ranks", displayName: "Ranks", group: "Membership", description: "Rank definitions." },
    { name: "membership_types", displayName: "Membership Types", group: "Membership", description: "Membership type options." },
    { name: "membership_statuses", displayName: "Membership Statuses", group: "Membership", description: "Membership status options." },
    { name: "survey_lodge_suggestions", displayName: "RSVP Lodge Suggestions", group: "Review", description: "Other lodge names entered on RSVP forms." },
];

const ALLOWED_COLUMNS = {
    constitutions: ["code", "description"],
    countries: ["code", "name"],
    ordercodes: ["code", "description"],
    lodges: ["code", "name"],
    eventtypes: ["code", "description"],
    documenttypes: ["code", "description"],
    filetypes: ["code", "description"],
    officers: ["role_name", "description"],
    degrees: ["name", "description"],
    ranks: ["rank_name", "description"],
    membership_types: ["name"],
    membership_statuses: ["name"],
    users: ["first_name", "last_name", "email", "username", "role"],
    survey_lodge_suggestions: ["lodge_name", "survey_title", "respondent_name", "respondent_email", "matched_lodge_name", "updated_at", "reviewed_at"],
};

function AdminPanel({ user }) {
    const [activeTable, setActiveTable] = useState("users");
    const [data, setData] = useState([]);
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [formData, setFormData] = useState({});
    const [isEditing, setIsEditing] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [confirmState, setConfirmState] = useState(null);
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(false);
    const [statusMessage, setStatusMessage] = useState("");

    const isUsersView = activeTable === "users";
    const activeConfig = TABLES.find((table) => table.name === activeTable) || TABLES[0];
    const columns = useMemo(() => ALLOWED_COLUMNS[activeTable] || [], [activeTable]);
    const canManageUsers = user?.role === "Admin" || user?.role === "Archivist";
    const isSuggestionView = activeTable === "survey_lodge_suggestions";
    const uid = (suffix) => `${activeTable}-${suffix}`;

    const fetchReferenceData = async () => {
        setLoading(true);
        setStatusMessage("");
        try {
            const response = await axios.get(isSuggestionView ? "/api/surveys/lodge-suggestions" : `/api/crud/${activeTable}`);
            const sortKey = columns.includes("name") ? "name" : columns.includes("description") ? "description" : columns[0];
            const sortedData = [...(response.data || [])].sort((a, b) =>
                String(a[sortKey] || "").localeCompare(String(b[sortKey] || ""))
            );
            setData(sortedData);
        } catch (error) {
            console.error("Error fetching data:", error);
            setStatusMessage("Could not load this reference table.");
        } finally {
            setLoading(false);
        }
    };

    const fetchUsersAndRoles = async () => {
        setLoading(true);
        setStatusMessage("");
        try {
            const [usersResponse, rolesResponse] = await Promise.all([
                axios.get("/api/users"),
                axios.get("/api/roles"),
            ]);
            const sortedUsers = [...(usersResponse.data || [])].sort((a, b) => {
                const aName = `${a.first_name || ""} ${a.last_name || ""}`.trim() || a.username || "";
                const bName = `${b.first_name || ""} ${b.last_name || ""}`.trim() || b.username || "";
                return aName.localeCompare(bName);
            });
            setUsers(sortedUsers);
            setRoles(rolesResponse.data || []);
        } catch (error) {
            console.error("Error fetching users and roles:", error);
            setStatusMessage("Could not load users or roles.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setSearch("");
        setShowModal(false);
        setConfirmState(null);
        if (isUsersView) fetchUsersAndRoles();
        else fetchReferenceData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTable]);

    const currentRows = isUsersView ? users : data;
    const filteredRows = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return currentRows;
        return currentRows.filter((row) =>
            columns.some((column) => {
                const value = column === "role" ? row.role_name : row[column];
                return String(value || "").toLowerCase().includes(term);
            })
        );
    }, [columns, currentRows, search]);

    const usersByRole = useMemo(() => {
        return users.reduce((acc, row) => {
            const key = row.role_name || "No role";
            acc[key] = (acc[key] || 0) + 1;
            return acc;
        }, {});
    }, [users]);

    const pendingCount = users.filter((row) =>
        /pending|not yet|unverified/i.test(row.role_name || "") || Number(row.role_id) === 1
    ).length;

    const tableGroups = useMemo(() => {
        return TABLES.reduce((acc, table) => {
            if (table.name === "users" && !canManageUsers) return acc;
            if (!acc[table.group]) acc[table.group] = [];
            acc[table.group].push(table);
            return acc;
        }, {});
    }, [canManageUsers]);

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setFormData((previous) => ({
            ...previous,
            [name]: name === "role_id" ? Number(value) || "" : value,
        }));
    };

    const openAddModal = () => {
        const initial = columns.reduce((acc, key) => {
            if (key !== "role") acc[key] = "";
            return acc;
        }, {});
        if (isUsersView) initial.role_id = "";
        setFormData(initial);
        setIsEditing(false);
        setEditingId(null);
        setShowModal(true);
    };

    const handleEdit = (row) => {
        setFormData({ ...row, role_id: row.role_id || "" });
        setIsEditing(true);
        setEditingId(row.id);
        setShowModal(true);
    };

    const saveRecord = async () => {
        setStatusMessage("");
        try {
            if (isUsersView) {
                if (!isEditing) {
                    setStatusMessage("Users should register themselves first; then assign their role here.");
                    return;
                }
                await axios.put(`/api/users/${editingId}`, formData);
                await fetchUsersAndRoles();
            } else {
                const url = isEditing ? `/api/crud/${activeTable}/${editingId}` : `/api/crud/${activeTable}`;
                const method = isEditing ? "put" : "post";
                await axios[method](url, formData);
                await fetchReferenceData();
            }
            setShowModal(false);
            setFormData({});
            setIsEditing(false);
            setEditingId(null);
            setStatusMessage("Saved.");
        } catch (error) {
            console.error("Error saving data:", error.response?.data || error);
            setStatusMessage(error.response?.data?.error || "Could not save this record.");
        }
    };

    const updateUserRole = async (row, roleId) => {
        if (!roleId) return;
        setStatusMessage("");
        try {
            await axios.put(`/api/users/${row.id}`, { ...row, role_id: Number(roleId) });
            await fetchUsersAndRoles();
            setStatusMessage(`Updated ${row.first_name || row.username || "user"}'s role.`);
        } catch (error) {
            console.error("Error updating role:", error);
            setStatusMessage("Could not update the user role.");
        }
    };

    const askDelete = (row) => {
        const label = isUsersView
            ? `${row.first_name || ""} ${row.last_name || ""}`.trim() || row.username || row.email || `User ${row.id}`
            : row.name || row.description || row.role_name || row.rank_name || row.code || `Record ${row.id}`;
        setConfirmState({ type: "delete", id: row.id, label });
    };

    const deleteRecord = async () => {
        if (!confirmState?.id) return;
        setStatusMessage("");
        try {
            await axios.delete(isUsersView ? `/api/users/${confirmState.id}` : `/api/crud/${activeTable}/${confirmState.id}`);
            if (isUsersView) {
                setUsers((previous) => previous.filter((row) => row.id !== confirmState.id));
            } else {
                setData((previous) => previous.filter((row) => row.id !== confirmState.id));
            }
            setConfirmState(null);
            setStatusMessage("Deleted.");
        } catch (error) {
            console.error("Error deleting data:", error.response?.data || error);
            setStatusMessage(error.response?.data?.message || error.response?.data?.error || "Could not delete this record.");
            setConfirmState(null);
        }
    };

    const renderCell = (row, key) => {
        if (key === "role") {
            return (
                <select
                    value={row.role_id || ""}
                    onChange={(event) => updateUserRole(row, event.target.value)}
                    id={`user-${row.id}-role-select`}
                    aria-label={`Select role for ${row.username || row.id}`}
                >
                    <option value="">Select Role</option>
                    {roles.map((role) => (
                        <option key={role.id} value={role.id}>
                            {role.name}
                        </option>
                    ))}
                </select>
            );
        }
        if (key === "survey_title") {
            return row.survey_title || row.survey_slug || <span className="admin-muted">Blank</span>;
        }
        if (key === "matched_lodge_name") {
            return row.matched_lodge_name
                ? `${row.matched_lodge_name}${row.matched_lodge_code ? ` No. ${row.matched_lodge_code}` : ""}`
                : <span className="admin-muted">No match</span>;
        }
        if (key === "updated_at" || key === "reviewed_at") {
            return row[key] ? new Date(row[key]).toLocaleString() : <span className="admin-muted">Not yet</span>;
        }
        return row[key] || <span className="admin-muted">Blank</span>;
    };

    const updateSuggestion = async (row, action) => {
        setStatusMessage("");
        try {
            await axios.patch(`/api/surveys/lodge-suggestions/${row.id}`, { action });
            await fetchReferenceData();
            setStatusMessage(action === "dismiss" ? "Suggestion dismissed." : "Suggestion marked reviewed.");
        } catch (error) {
            console.error("Error updating RSVP lodge suggestion:", error.response?.data || error);
            setStatusMessage(error.response?.data?.error || "Could not update the suggestion.");
        }
    };

    return (
        <div className="admin-panel">
            <section className="admin-hero">
                <div>
                    <p className="admin-kicker">Site control</p>
                    <h1>Administration</h1>
                    <p>Manage users, access roles, and the lookup data that keeps the archive consistent.</p>
                </div>
                <div className="admin-hero-card">
                    <span>Signed in as</span>
                    <strong>{user?.firstName || user?.username || "Administrator"}</strong>
                    <small>{user?.role || "Role unknown"}</small>
                </div>
            </section>

            <section className="admin-summary-grid">
                <article>
                    <span>Users</span>
                    <strong>{users.length}</strong>
                    <small>{pendingCount} pending review</small>
                </article>
                <article>
                    <span>Roles</span>
                    <strong>{roles.length}</strong>
                    <small>{Object.keys(usersByRole).length || 0} in use</small>
                </article>
                <article>
                    <span>{activeConfig.displayName}</span>
                    <strong>{currentRows.length}</strong>
                    <small>{filteredRows.length} shown</small>
                </article>
                <article>
                    <span>Workspace</span>
                    <strong>{activeConfig.group}</strong>
                    <small>{activeConfig.description}</small>
                </article>
            </section>

            <div className="admin-workspace">
                <aside className="admin-sidebar">
                    {Object.entries(tableGroups).map(([group, groupTables]) => (
                        <div className="admin-nav-group" key={group}>
                            <h2>{group}</h2>
                            {groupTables.map((table) => (
                                <button
                                    key={table.name}
                                    className={activeTable === table.name ? "active" : ""}
                                    type="button"
                                    onClick={() => setActiveTable(table.name)}
                                >
                                    <span>{table.displayName}</span>
                                    <small>{table.description}</small>
                                </button>
                            ))}
                        </div>
                    ))}
                </aside>

                <main className="admin-main">
                    <div className="admin-toolbar">
                        <div>
                            <p className="admin-kicker">{activeConfig.group}</p>
                            <h2>{activeConfig.displayName}</h2>
                            <p>{activeConfig.description}</p>
                        </div>
                        <div className="admin-toolbar-actions">
                            <input
                                value={search}
                                onChange={(event) => setSearch(event.target.value)}
                                placeholder={`Search ${activeConfig.displayName.toLowerCase()}...`}
                                aria-label="Search admin records"
                            />
                            <button className="admin-btn secondary" type="button" onClick={isUsersView ? fetchUsersAndRoles : fetchReferenceData}>
                                Refresh
                            </button>
                            {!isUsersView && !isSuggestionView && (
                                <button
                                    className="admin-btn primary"
                                    type="button"
                                    onClick={openAddModal}
                                    id={uid("btn-add-new")}
                                >
                                    Add New
                                </button>
                            )}
                        </div>
                    </div>

                    {statusMessage && <div className="admin-status">{statusMessage}</div>}

                    {isUsersView && (
                        <div className="admin-role-strip">
                            {Object.entries(usersByRole).map(([role, count]) => (
                                <span key={role}>{role}: <strong>{count}</strong></span>
                            ))}
                        </div>
                    )}

                    <div className="admin-table-wrap">
                        <table className="data-table" id={uid("data-table")}>
                            <thead>
                            <tr>
                                {columns.map((key) => (
                                    <th key={key}>{key.replace(/_/g, " ")}</th>
                                ))}
                                <th>Actions</th>
                            </tr>
                            </thead>
                            <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={columns.length + 1}>Loading...</td>
                                </tr>
                            ) : filteredRows.length ? (
                                filteredRows.map((row) => (
                                    <tr key={row.id || row.code}>
                                        {columns.map((key) => (
                                            <td key={key}>{renderCell(row, key)}</td>
                                        ))}
                                        <td>
                                            <div className="admin-row-actions">
                                                {isSuggestionView ? (
                                                    <>
                                                        <button className="admin-btn compact" type="button" onClick={() => updateSuggestion(row, "review")}>
                                                            Mark Reviewed
                                                        </button>
                                                        <button className="admin-btn danger compact" type="button" onClick={() => updateSuggestion(row, "dismiss")}>
                                                            Dismiss
                                                        </button>
                                                    </>
                                                ) : (
                                                    <>
                                                        <button className="admin-btn compact" type="button" onClick={() => handleEdit(row)}>
                                                            Edit
                                                        </button>
                                                        <button className="admin-btn danger compact" type="button" onClick={() => askDelete(row)}>
                                                            Delete
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={columns.length + 1}>No matching records.</td>
                                </tr>
                            )}
                            </tbody>
                        </table>
                    </div>
                </main>
            </div>

            {showModal && (
                <div className="modal-overlay" id={uid("modal-overlay-edit")}>
                    <div className="modal-content admin-modal-content" role="dialog" aria-modal="true" aria-labelledby={uid("modal-title-edit")}>
                        <div className="admin-modal-header">
                            <div>
                                <p className="admin-kicker">{activeConfig.displayName}</p>
                                <h3 id={uid("modal-title-edit")}>{isEditing ? "Edit Record" : "Add Record"}</h3>
                            </div>
                            <button className="admin-icon-btn" type="button" onClick={() => setShowModal(false)} aria-label="Close">×</button>
                        </div>

                        <div className="admin-form-grid">
                            {columns.map((key) => (
                                <div key={key} className="form-group">
                                    <label htmlFor={uid(`field-${key}`)}>{key.replace(/_/g, " ")}</label>
                                    {key === "role" ? (
                                        <select
                                            id={uid(`field-${key}`)}
                                            name="role_id"
                                            value={formData.role_id || ""}
                                            onChange={handleInputChange}
                                        >
                                            <option value="">Select Role</option>
                                            {roles.map((role) => (
                                                <option key={role.id} value={role.id}>
                                                    {role.name}
                                                </option>
                                            ))}
                                        </select>
                                    ) : (
                                        <input
                                            id={uid(`field-${key}`)}
                                            name={key}
                                            value={formData[key] || ""}
                                            onChange={handleInputChange}
                                        />
                                    )}
                                </div>
                            ))}
                        </div>

                        <div className="modal-actions">
                            <button className="admin-btn primary" type="button" onClick={saveRecord}>
                                Save
                            </button>
                            <button className="admin-btn secondary" type="button" onClick={() => setShowModal(false)}>
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {confirmState && (
                <div className="modal-overlay" id={uid("modal-overlay-confirm")}>
                    <div className="modal-content admin-confirm-content" role="dialog" aria-modal="true">
                        <h3>Delete record?</h3>
                        <p>
                            This will permanently delete <strong>{confirmState.label}</strong>.
                        </p>
                        <div className="modal-actions">
                            <button className="admin-btn danger" type="button" onClick={deleteRecord}>
                                Delete
                            </button>
                            <button className="admin-btn secondary" type="button" onClick={() => setConfirmState(null)}>
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AdminPanel;
